/**
 * EventBus — a tiny, dependency-free pub/sub used to keep the game systems,
 * the 3D world and the DOM HUD decoupled. Systems never import each other;
 * they talk through named events on a single shared bus instance.
 *
 * Usage:
 *   bus.on('round:win', (payload) => { ... });
 *   const off = bus.on('tick', fn);   // returns an unsubscribe fn
 *   bus.emit('round:win', { amount: 12.4 });
 *   off();
 */
export default class EventBus {
  constructor() {
    /** @type {Map<string, Set<Function>>} */
    this._handlers = new Map();
  }

  /**
   * Subscribe to an event.
   * @returns {() => void} unsubscribe function
   */
  on(type, handler) {
    if (typeof handler !== 'function') throw new TypeError('handler must be a function');
    let set = this._handlers.get(type);
    if (!set) this._handlers.set(type, (set = new Set()));
    set.add(handler);
    return () => this.off(type, handler);
  }

  /** Subscribe once; auto-unsubscribes after the first emit. */
  once(type, handler) {
    const off = this.on(type, (payload) => {
      off();
      handler(payload);
    });
    return off;
  }

  off(type, handler) {
    const set = this._handlers.get(type);
    if (set) {
      set.delete(handler);
      if (set.size === 0) this._handlers.delete(type);
    }
  }

  /** Fire an event. Handlers are copied first so on/off during emit is safe. */
  emit(type, payload) {
    const set = this._handlers.get(type);
    if (!set || set.size === 0) return;
    for (const handler of [...set]) {
      try {
        handler(payload);
      } catch (err) {
        // A broken listener must never take down the frame loop.
        console.error(`[EventBus] listener for "${type}" threw:`, err);
      }
    }
  }

  clear() {
    this._handlers.clear();
  }
}
